import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import ViewLayout from "@/v2/layout/View";
import { CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Calendar, Clock3, FileText, User, Wallet, Pencil } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import FindOnePayslip from "@/v2/api/payslip/FindOne";
import AccessControl from "@/v2/components/rbac/AccessControl";
import PayslipDeleteButton from "@/v2/components/ui/button/delete/Payslip";
import PrintFreelancerPayslipPDF from "@/v2/components/pages/payslip/freelancer/Print";
import { ElementType, ReactNode } from "react";

const formatDate = (date?: string | null) =>
  date ? format(new Date(date), "dd MMM yyyy") : "N/A";

const DetailItem = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: ReactNode;
  icon?: ElementType;
}) => (
  <div className="space-y-2">
    <div className="flex items-center text-sm text-muted-foreground">
      {Icon && <Icon className="mr-2 h-4 w-4" />}
      {label}
    </div>

    <div className="text-base font-medium">{value ?? "N/A"}</div>
  </div>
);

const NavButtons = ({ id }: { id: string }) => {
  return (
    <>
      <AccessControl module="PAYSLIP_MANAGEMENT" requestedAccess={"PUT"}>
        <Link to={`/payslips/freelancer/edit/${id}`}>
          <Button variant={"outline"} className="p-4" size={"sm"}>
            <Pencil className="h-4 w-4" />
            {"Edit"}
          </Button>
        </Link>
      </AccessControl>
      <PrintFreelancerPayslipPDF payslipId={id} isInside />
      <AccessControl
        module="PAYSLIP_MANAGEMENT"
        requestedAccess={"DELETE"}
      >
        <PayslipDeleteButton id={id} isInside />
      </AccessControl>
    </>
  );
};

export default function ViewOneFreelancePayslip() {
  const { id } = useParams();
  const navigate = useNavigate();

  if (!id) navigate(-1);

  const query = useQuery({
    queryKey: ["salary", id],
    queryFn: () => FindOnePayslip(id!),
  });

  if (query.isLoading || query.isFetching) {
    return <LoadingAnimation />;
  }

  if (!query.data) {
    return <p>Something went wrong.</p>;
  }

  const salary = query.data;

  return (
    <ViewLayout
      title="Salary Details"
      description="Payroll information"
      icon={Wallet}
      buttons={<NavButtons id={id!} />}
    >
      <CardContent className="space-y-8">
        {/* Salary Information */}
        <section>
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <Wallet className="h-5 w-5" />
            Salary Information
          </h3>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <DetailItem
              label="Employee Name"
              value={salary.employee_name}
              icon={User}
            />

            <DetailItem
              label="Salary Type"
              value={
                salary.type === "HOURLY" ? (
                  <Badge>Freelance</Badge>
                ) : (
                  salary.type
                )
              }
              icon={Wallet}
            />

            <DetailItem
              label="Month"
              value={salary.month}
              icon={Calendar}
            />

            <DetailItem
              label="Year"
              value={salary.year}
              icon={Calendar}
            />

            <DetailItem
              label="Hours Worked"
              value={salary.total_hours}
              icon={Clock3}
            />

            <DetailItem
              label="Hourly Rate"
              value={salary.hourly_rate}
            />

            <DetailItem
              label="Total Deduction"
              value={salary.deduction}
            />

            <DetailItem label="Amount" value={salary.amount} />
          </div>
        </section>

        <Separator />

        {/* Employee Information */}
        <section>
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <User className="h-5 w-5" />
            Employee Information
          </h3>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <DetailItem
              label="Iqama"
              value={salary.employee.iqama}
              icon={User}
            />

            <DetailItem
              label="Phone"
              value={salary.employee.phone_number}
              icon={User}
            />

            <DetailItem
              label="Nationality"
              value={salary.employee?.nationality}
              icon={User}
            />

            <DetailItem
              label="Location"
              value={salary.employee?.location}
              icon={User}
            />

            <DetailItem
              label="ID Expiry"
              value={formatDate(salary.employee?.id_expiry)}
              icon={Calendar}
            />
          </div>
        </section>

        <Separator />

        {/* Notes */}
        <section>
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <FileText className="h-5 w-5" />
            Notes
          </h3>

          <div className="space-y-6">
            <DetailItem
              label="Food Allowance Note"
              value={salary.food_allowance_note || "N/A"}
            />

            <DetailItem
              label="General Note"
              value={salary.note || "N/A"}
            />
          </div>
        </section>
      </CardContent>
    </ViewLayout>
  );
}
